import React, { useState, useRef, useEffect } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useIntersectionObserverCallback } from '@navi-toolbox/hooks';
import { Typography, Skeleton } from 'fronton-react';
import { ChevronRightSmallIcon } from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getInfo,
  isCalculatorAvailable,
  isCalculatorParametersLoading,
} from 'features/product-info/ducks/selectors';
import { ProductMainInfoProps } from 'features/product-info';
import { CALCULATE_QUANTITY_GA } from 'features/ga-events/messages/ga-calculate-quantity-open.pdp';
import { CALCULATE_QUANTITY_GA_SHOW } from 'features/ga-events/messages/ga-calculate-quantity-show.pdp';
import { RootReducer } from 'store/root-reducer';
import { getArticle } from 'common/selectors';
import { createGetExperiment } from 'features/page-model';
import { ConnectedProps } from 'types/utils';
import { HIDE_CALCULATOR_INTERNAL_ID } from 'common/constants';
import { PdpCalculatorModal } from '../pdp-calculator-modal';
import {
  DATA_TESTID_CALCULATE_QUANTITY_BUTTON,
  CALCULATORS_LAMINATE_PROJECT_UPDATED_EVENT,
} from './constants';
import {
  calculationChevronWrapper,
  calculationQuantityTypography,
  calculatorQuantityWrapper,
} from './styles';
import { CalculateQuantityIcon } from 'common/icons/calculate-quantity-icon';
import {
  getLaminateConfiguration,
  ProjectConfigurationParameters,
  configurationsActions,
  getLaminateConfigurationLoading,
} from 'features/configurations';
import {
  getArea,
  getRoomTypeTitle,
  isPossibleToCalculateProjectArea,
  parseProjectConfiguration,
} from './utils';

type CalculateQuantityMappedState = {
  isCalcAvailable: boolean;
  productId: string | null;
  productInfo: ProductMainInfoProps | null;
  projectConfiguration: ProjectConfigurationParameters;
  isProjectInfoLoading: boolean;
  isShowProjectConfigurationData: boolean;
};

type CalculateQuantityProps = ConnectedProps<CalculateQuantityMappedState, typeof mapDispatchToProps>;

export const CalculateQuantity: React.FC<CalculateQuantityProps> = ({
  isCalcAvailable,
  productInfo,
  projectConfiguration,
  isProjectInfoLoading,
  isShowProjectConfigurationData,
  gaCalculateQuantityShow,
  gaCalculateQuantityOpen,
  setLaminateConfiguration,
}) => {
  const { t } = useTranslation();
  const [isShowCalculatorModal, setIsShowCalculatorModal] = useState<boolean>(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const parsedProjectConfiguration = parseProjectConfiguration(projectConfiguration);
  const projectConfigurationRoomType = getRoomTypeTitle(parsedProjectConfiguration.roomType, t);
  const projectConfigurationRoomArea = getArea(parsedProjectConfiguration);
  const projectConfigurationTitle = `${projectConfigurationRoomType} ${projectConfigurationRoomArea} ${t('calculator.ui.area-unit')}`;
  const buttonTitle = isShowProjectConfigurationData
    ? projectConfigurationTitle
    : t('calculator.ui.calculate');
  const buttonDescription = isShowProjectConfigurationData
    ? t('calculator.ui.add-tools')
    : t('calculator.ui.with-tools');

  useEffect(() => {
    const cb = (e: Event) => {
      setLaminateConfiguration((e as CustomEvent).detail.projectConfigurationParameters);
    };
    document.body.addEventListener(CALCULATORS_LAMINATE_PROJECT_UPDATED_EVENT, cb);

    return () => {
      document.body.removeEventListener(CALCULATORS_LAMINATE_PROJECT_UPDATED_EVENT, cb);
    };
  }, [setLaminateConfiguration]);

  useIntersectionObserverCallback(
    buttonRef,
    {
      enter: () => {
        gaCalculateQuantityShow({ elementText: buttonTitle });
      },
      extraUnobserve: 'enter',
    },
    [productInfo],
  );

  const handleToggleModal = () => {
    setIsShowCalculatorModal((prev) => !prev);
    if (!isShowCalculatorModal) {
      gaCalculateQuantityOpen({ elementText: buttonTitle });
    }
  };

  let content: React.ReactNode = null;

  if (isCalcAvailable) {
    content = (
      <div data-testid="calculate-quantity_mf-pdp" ref={buttonRef}>
        <Skeleton variant={'control'} showLoading={isProjectInfoLoading} width={'100%'}>
          <div
            className={calculatorQuantityWrapper}
            data-testid={DATA_TESTID_CALCULATE_QUANTITY_BUTTON}
            onClick={handleToggleModal}
          >
            <CalculateQuantityIcon />
            <div className={calculationQuantityTypography}>
              <Typography size="body_accent" variant="s">
                {buttonTitle}
              </Typography>
              <Typography size="regular" variant="mini">
                {buttonDescription}
              </Typography>
            </div>
            <div className={calculationChevronWrapper}>
              <ChevronRightSmallIcon type="outline" size="m" color="text-minor" />
            </div>
          </div>
        </Skeleton>
        <PdpCalculatorModal isShowed={isShowCalculatorModal} onCloseModal={handleToggleModal} />
      </div>
    );
  }

  return <>{content}</>;
};

export const mapStoreToProps = (store: RootReducer): CalculateQuantityMappedState => {
  const isCalcHidden = createGetExperiment(HIDE_CALCULATOR_INTERNAL_ID)(store);
  const isCalcAvailable = (isCalculatorAvailable(store) && !isCalcHidden) ?? false;
  const productId = getArticle(store);
  const productInfo = getInfo(store);
  const projectConfiguration = getLaminateConfiguration(store);
  const projectConfigurationLoading = getLaminateConfigurationLoading(store);
  const isCalculatorParamsLoading = isCalculatorParametersLoading(store);
  const isProjectInfoLoading = projectConfigurationLoading || isCalculatorParamsLoading;
  const isShowProjectConfigurationData = isPossibleToCalculateProjectArea(projectConfiguration);

  return {
    isCalcAvailable,
    productId,
    productInfo,
    projectConfiguration,
    isProjectInfoLoading,
    isShowProjectConfigurationData,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  gaCalculateQuantityOpen: ({ elementText }: { elementText: string }) => {
    dispatch(CALCULATE_QUANTITY_GA({ elementText }));
  },
  gaCalculateQuantityShow: ({ elementText }: { elementText: string }) => {
    dispatch(CALCULATE_QUANTITY_GA_SHOW({ elementText }));
  },
  setLaminateConfiguration: (projectConfigurationParameters: ProjectConfigurationParameters) => {
    dispatch(configurationsActions.SET_LAMINATE_CONFIGURATION(projectConfigurationParameters));
  },
});

export const PdpCalculateQuantity = connect(mapStoreToProps, mapDispatchToProps)(CalculateQuantity);
